/**
 * World skills (RFC 0261 slice 4): the deployment's shared skill catalog, and
 * the tools that author it.
 *
 * WHAT A SKILL IS HERE. A catalog entry in the World — title, description, link
 * — pointing at an ordinary Mod document holding the instructions. Nothing
 * more. The agent sees every skill's one-line description in its system prompt
 * and reads the document with `read_document` only when the task matches, so a
 * World can carry fifty skills at the cost of fifty lines rather than fifty
 * documents' worth of context. That is searugast's `<available_skills>` design,
 * and `formatAvailableApps` (RFC 0267 §5d) is the same shape applied to apps.
 *
 * WHY THE WORLD AND NOT THE PROJECT. "How we write plans" is not a fact about
 * one project. Hanging it off a project would mean re-writing it in the next
 * one, and hanging it off a checkout (`.claude/skills/...`) reaches exactly one
 * agent: a Claude Code run whose working directory is that checkout. The World
 * is the layer every project on the deployment already shares, so a skill
 * registered once is served to every session, on every device, in every
 * project. That stays the default. A skill that only matters in one project,
 * or should reach the people shared into it, is registered with
 * `scope: 'project'` into that project's own catalog (RFC 0334 3a), and
 * `resolveSkills` lets it replace a World skill of the same id there.
 *
 * WHY THE CONTENT IS A MOD DOCUMENT. Because you have to be able to EDIT it.
 * searugast gives skills their own `SkillDoc` shape; a document is Mod's
 * equivalent that an existing panel already renders, an existing tool already
 * reads, and `patch_document` already edits. The catalog entry is World-scoped
 * even though the document is catalogued in the project it was authored in —
 * `read_document` resolves by id and never consults the project catalog, so
 * every session reaches it regardless of where it was written.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { moveToProject } from '../model/documents/move-project'
import {
  createDocument,
  moveDocument,
  touchCatalog,
  writeDocumentContent,
} from '../model/documents/mutations'
import { projectDocuments } from '../model/documents/reads'
import type { DocumentDoc } from '../model/documents/types'
import {
  addProjectSkill,
  setProjectInstructions,
} from '../model/project/mutations'
import { reconcileSkillCatalog } from '../model/skills/reconcile'
import {
  addWorldSkill,
  ensureWorldHomeProject,
  setWorldAgentsMd,
  skillIdFromTitle,
} from '../model/world/mutations'
import { projectWorldSkills, type WorldSkillRef } from '../model/world/reads'
import { isWorldSoulHome, readStandingText } from '../model/world/standing'
import { formatModCapabilities } from './capability-guide'
import type { ToolDef } from './project-tools'
import {
  formatListedSkills,
  readDocBounded,
  resolveSkills,
} from './resolve-skills'

/**
 * The `<available_skills>` block for a World's skills alone, searugast's
 * `system_prompt.rs` in shape. A host serving a turn uses the resolved set
 * (`readSkillsTurnContext`, RFC 0334 3c) instead; this is the World-only view
 * of the same format, '' for a World with no skills.
 */
export function formatAvailableSkills(worldDoc: unknown): string {
  return formatListedSkills(resolveSkills(worldDoc, null, [], []))
}

/**
 * This World's standing instructions block (searugast `agents_md`), or '' when
 * blank. Unlike a skill this is served on EVERY turn, so it is for the handful
 * of things that are always true, not for task-specific guidance.
 *
 * TAKES THE TEXT, NOT THE DOC (RFC 0334 3g). The soul file can live in a
 * document, so reading `agentsMd` off the World can be stale; read the text
 * with `readStandingText` and pass it here.
 */
export function formatWorldInstructions(text: string): string {
  const t = text.trim()
  return t ? `<world_instructions>\n${t}\n</world_instructions>` : ''
}

/**
 * This project's standing instructions block (RFC 0334 3b), or '' when blank:
 * every agent in this project reads it, no agent in another project does.
 * Takes the text, read with `readStandingText`, for the same reason.
 */
export function formatProjectInstructions(text: string): string {
  const t = text.trim()
  return t ? `<project_instructions>\n${t}\n</project_instructions>` : ''
}

/**
 * The standing instructions below Mod's own block (RFC 0334 3b): the World's,
 * then the project's. Nearer comes LATER, so where the two disagree the
 * project's is what the agent read last. '' when neither is set.
 */
export function formatStandingInstructions(
  worldText: string,
  projectText: string,
): string {
  return [
    formatWorldInstructions(worldText),
    formatProjectInstructions(projectText),
  ]
    .filter(Boolean)
    .join('\n\n')
}

/**
 * Create or update a skill: write its document, then catalog it (RFC 0261
 * slice 4).
 *
 * The ONE implementation, shared by `register_skill`, the cli's `skills add`,
 * and the composer's New skill. Three copies of "make a document, then add a
 * World entry" is three chances to write one without the other, which leaves
 * either a skill nothing serves or a catalog entry pointing at nothing.
 *
 * Re-registering an existing id REWRITES that skill's document rather than
 * creating a second one. A second would sit in the sidebar, no longer be the
 * skill anyone is served, and have nothing saying which of the two is live.
 *
 * `scope` picks the catalog (RFC 0334 3a): the World's, the default, or the
 * project's. "Existing" is looked up in THAT catalog only, so a project skill
 * named like a World skill gets its own document and shadows the World's here,
 * rather than rewriting the text every other project is served.
 */
export async function createSkill(
  repo: Repo,
  opts: {
    worldId: string
    /** Where a NEWLY written document is catalogued. Unused when `documentId`
     *  names one that already exists. */
    projectId: string
    title: string
    description: string
    /** The skill text. Omit only when passing `documentId`. */
    instructions?: string
    /** Catalog an existing document instead of writing one. */
    documentId?: string
    /** Defaults to a slug of the title. */
    skillId?: string
    /** Which catalog holds the entry. Defaults to the World. */
    scope?: SkillCatalogScope
  },
): Promise<WorldSkillRef> {
  const scope = opts.scope ?? 'world'
  const skillId = opts.skillId?.trim() || skillIdFromTitle(opts.title)
  const catalogId = scope === 'project' ? opts.projectId : opts.worldId
  const existing = await findSkill(repo, catalogId, skillId)
  const homeProjectId =
    scope === 'world' ? await ensureWorldHomeProject(repo, opts.worldId) : null
  const destinationId = homeProjectId ?? opts.projectId
  const packagePath = `skills/${skillId}/SKILL.md`

  let documentId = opts.documentId?.trim() ?? ''
  if (!documentId) {
    const destination = await repo.find(destinationId as AnyDocumentId)
    await (destination as { whenReady?: () => Promise<void> }).whenReady?.()
    const atPath = projectDocuments(destination.doc()).filter(
      (ref) => ref.path === packagePath,
    )
    if (atPath.length > 1)
      throw new Error(`${packagePath} has more than one document`)
    if (atPath.length) documentId = atPath[0].id
  }
  if (!documentId && existing) documentId = existing.documentId
  if (documentId) {
    const handle = await repo.find<DocumentDoc>(documentId as AnyDocumentId)
    await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
    const sourceId = String(handle.doc()?.workspaceId ?? '')
    if (!sourceId) throw new Error(`${documentId} has no owning project`)
    if (sourceId !== destinationId) {
      await moveToProject(
        repo,
        sourceId,
        destinationId,
        { documentId, path: packagePath },
        { worldId: opts.worldId },
      )
    } else {
      await moveDocument(repo, destinationId, documentId, packagePath)
    }
  }
  if (!documentId) {
    const handle = await createDocument(repo, destinationId, {
      path: packagePath,
      mime: 'text/markdown',
      title: opts.title,
      content: opts.instructions ?? '',
    })
    documentId = handle.documentId as string
  } else if (!opts.documentId && opts.instructions !== undefined) {
    await writeDocumentContent(
      repo,
      destinationId,
      documentId,
      opts.instructions,
    )
  }

  const skillDoc = await repo.find<DocumentDoc>(documentId as AnyDocumentId)
  await (skillDoc as { whenReady?: () => Promise<void> }).whenReady?.()
  const current = skillDoc.doc()
  if (
    current?.skill?.title !== opts.title ||
    current?.skill?.description !== opts.description
  ) {
    const at = Date.now()
    skillDoc.change((d) => {
      d.skill = { title: opts.title, description: opts.description }
      d.updatedAt = at
    })
    await touchCatalog(repo, destinationId, documentId, at)
  }

  const add = scope === 'project' ? addProjectSkill : addWorldSkill
  await add(repo, catalogId, skillId, {
    title: opts.title,
    description: opts.description,
    documentId,
    sourceUpdatedAt: skillDoc.doc().updatedAt,
  })
  return {
    skillId,
    title: opts.title,
    description: opts.description,
    documentId,
  }
}

/** Move pre-0357 World skill documents into canonical home-project packages.
 * Re-running is safe: document ids and catalog ids stay unchanged. */
export async function ensureWorldSkillPackages(
  repo: Repo,
  worldId: string,
): Promise<string[]> {
  const world = await repo.find(worldId as AnyDocumentId)
  await (world as { whenReady?: () => Promise<void> }).whenReady?.()
  const homeProjectId = await ensureWorldHomeProject(repo, worldId)
  const moved: string[] = []
  for (const skill of projectWorldSkills(world.doc())) {
    const doc = await repo.find<DocumentDoc>(skill.documentId as AnyDocumentId)
    await (doc as { whenReady?: () => Promise<void> }).whenReady?.()
    const canonicalPath = `skills/${skill.skillId}/SKILL.md`
    const wasMoved =
      String(doc.doc()?.workspaceId ?? '') !== homeProjectId ||
      String(doc.doc()?.path ?? '') !== canonicalPath
    // Metadata marks a package that was already migrated. A person moving its
    // SKILL.md out of skills/ is removing it, not asking us to move it back.
    if (wasMoved && doc.doc()?.skill) continue
    if (!wasMoved && doc.doc()?.skill) continue
    await createSkill(repo, {
      worldId,
      projectId: homeProjectId,
      ...skill,
    })
    if (wasMoved) moved.push(skill.skillId)
  }
  await reconcileSkillCatalog(repo, worldId, homeProjectId)
  return moved
}

/** Migrate old project skill links into ordinary packages without changing ids. */
export async function ensureProjectSkillPackages(
  repo: Repo,
  worldId: string,
  projectId: string,
): Promise<string[]> {
  const project = await repo.find(projectId as AnyDocumentId)
  await (project as { whenReady?: () => Promise<void> }).whenReady?.()
  const moved: string[] = []
  for (const skill of projectWorldSkills(project.doc())) {
    const doc = await repo.find<DocumentDoc>(skill.documentId as AnyDocumentId)
    await (doc as { whenReady?: () => Promise<void> }).whenReady?.()
    const canonicalPath = `skills/${skill.skillId}/SKILL.md`
    const wasMoved =
      String(doc.doc()?.workspaceId ?? '') !== projectId ||
      String(doc.doc()?.path ?? '') !== canonicalPath
    if (wasMoved && doc.doc()?.skill) continue
    if (!wasMoved && doc.doc()?.skill) continue
    await createSkill(repo, {
      worldId,
      projectId,
      ...skill,
      scope: 'project',
    })
    if (wasMoved) moved.push(skill.skillId)
  }
  await reconcileSkillCatalog(repo, projectId, projectId)
  return moved
}

/** The catalogs a person or agent registers into. Mod and app skills are not
 *  registered: Mod's ship in core, an app's comes with the app. */
export type SkillCatalogScope = 'world' | 'project'

/** One skill by catalog id, or null. `catalogId` is the World or the project;
 *  both carry `skills` in one shape. Hydrates the doc, so callers that are
 *  about to write to it pay the find once. */
async function findSkill(
  repo: Repo,
  catalogId: string,
  skillId: string,
): Promise<WorldSkillRef | null> {
  const handle = await repo.find(catalogId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  return (
    projectWorldSkills(handle.doc()).find((s) => s.skillId === skillId) ?? null
  )
}

/**
 * The skill-authoring tools, for a host that knows which World it serves.
 *
 * `projectId` is where a newly written skill's DOCUMENT is catalogued (it has
 * to be catalogued somewhere to be listed and edited in a UI), and the project
 * whose catalog a `scope: 'project'` ENTRY goes into. A World entry, the
 * default, serves every project.
 *
 * ONE TOOL, and the omissions are the design.
 *
 * No `list_skills`, for the reason there is no `list_apps`: the
 * `<available_skills>` block already puts every skill and its document id in
 * the agent's context, so a tool would duplicate the prompt.
 *
 * No `unregister_skill`. Removing a skill is rare, it is deployment-wide, and
 * `mod-next skills rm` does it. Registering is the one that has to be reachable
 * mid-conversation, because "write that down as a skill" is something a person
 * says while working; nobody says "un-register that" mid-task.
 *
 * No edit tool: a skill is a document, so `patch_document` on the id the prompt
 * already gave it is the edit. The document tools ARE the skill tools.
 *
 * Every tool costs context on every turn of every session, and the agent
 * already carries around twenty. A tool that earns its slot is one the
 * conversation reaches for.
 */
export function skillTools(
  repo: Repo,
  opts: { worldId: string; projectId: string },
): Record<string, ToolDef> {
  return {
    /**
     * The World's standing instructions, which `setWorldAgentsMd` has been able
     * to write since it was ported and which nothing could call.
     *
     * TWO ACTIONS, NOT ONE WITH A DEFAULT. The write replaces the text
     * wholesale, because it is one authored document and not a merge of
     * opinions, so a tool where omitting an argument silently reads instead of
     * writing (or worse, writes an empty string) is the one shape this must not
     * have. Read first, then write what you intend to stand.
     */
    world_instructions: {
      description:
        'Standing instructions, served on every turn (an AGENTS.md). `scope` ' +
        '"world" (the default) is read by EVERY session in this World; ' +
        '"project" by every session in this project only, after the World’s. ' +
        '`action`: "get" returns the current text, "set" REPLACES it ' +
        'wholesale with `instructions`. For the handful of things always true ' +
        'there: how to write, what to check before claiming something works. ' +
        'Task-specific guidance is a SKILL instead (register_skill), because ' +
        'a skill is read when it matches and this is read every turn by ' +
        'everyone. Get before you set: a set discards whatever was there.',
      inputSchema: {
        type: 'object',
        properties: {
          action: { type: 'string', description: 'get | set' },
          instructions: {
            type: 'string',
            description:
              'The full replacement text (markdown), for action "set".',
          },
          scope: {
            type: 'string',
            description:
              'world (default): every project. project: this project only.',
          },
        },
        required: ['action'],
      },
      execute: async (a) => {
        const action = String(a.action ?? '').trim()
        const scope = String(a.scope ?? '').trim() || 'world'
        if (scope !== 'world' && scope !== 'project') {
          throw new Error(`unknown scope "${scope}" (world | project)`)
        }
        const docId = scope === 'project' ? opts.projectId : opts.worldId
        const handle = await repo.find(docId as AnyDocumentId)
        await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
        // The document once there is one (RFC 0334 3g), else the string.
        const current = await readStandingText(repo, handle.doc(), scope)
        if (action === 'get') return current || '(no standing instructions set)'
        if (action !== 'set') {
          throw new Error(`unknown action "${action}" (get | set)`)
        }
        const next = String(a.instructions ?? '')
        if (!next.trim()) {
          throw new Error(
            'instructions is required for set; to clear them, pass a single "-"',
          )
        }
        const text = next.trim() === '-' ? '' : next
        if (scope === 'project') {
          await setProjectInstructions(repo, opts.projectId, text)
        } else {
          await setWorldAgentsMd(repo, opts.worldId, text)
        }
        const whose = scope === 'project' ? "project's" : "World's"
        const reach =
          scope === 'project'
            ? 'Every session in this project, and no other,'
            : 'Every session in this World'
        return (
          `Set the ${whose} standing instructions (${next.length} chars, was ` +
          `${current.length}). ${reach} reads them on its next fresh start.`
        )
      },
    },
    register_skill: {
      description:
        'Register a SKILL: reusable instructions for a kind of task ("how we ' +
        'write plans"). `scope` "world" (the default) serves it in every ' +
        'project; "project" serves it in this project only, to everyone in ' +
        'it, and replaces a World skill of the same skillId here. Pass ' +
        '`instructions` to write a new skill document, or `documentId` to ' +
        'register a document that already exists. Registering an existing ' +
        'skillId in the same scope updates it in place. To change a skill’s ' +
        'TEXT later, patch its document — do not re-register.',
      inputSchema: {
        type: 'object',
        properties: {
          title: {
            type: 'string',
            description: 'Short name, e.g. "Writing plans"',
          },
          description: {
            type: 'string',
            description:
              'When to use this skill, in one line. The agent chooses on this ' +
              'alone, so describe the TASK ("writing or restructuring a plan ' +
              'document"), not the file.',
          },
          instructions: {
            type: 'string',
            description:
              'The full skill text (markdown). Omit only when passing documentId.',
          },
          documentId: {
            type: 'string',
            description:
              'Register an EXISTING document as this skill, instead of writing one.',
          },
          skillId: {
            type: 'string',
            description:
              'Stable catalog id. Defaults to a slug of the title; pass the ' +
              'existing id to update a skill rather than add a second one.',
          },
          scope: {
            type: 'string',
            description:
              'world (default): every project. project: this project only.',
          },
        },
        required: ['title', 'description'],
      },
      execute: async (a) => {
        const title = String(a.title ?? '').trim()
        const description = String(a.description ?? '').trim()
        if (!title) throw new Error('title is required')
        if (!description) throw new Error('description is required')
        const instructions = String(a.instructions ?? '')
        const given = String(a.documentId ?? a.document_id ?? '').trim()
        if (!given && !instructions.trim()) {
          throw new Error(
            'pass instructions (to write the skill) or documentId (to register an existing document)',
          )
        }
        const skillId =
          String(a.skillId ?? a.skill_id ?? '').trim() ||
          skillIdFromTitle(title)
        const scope = String(a.scope ?? '').trim() || 'world'
        if (scope !== 'world' && scope !== 'project') {
          throw new Error(`unknown scope "${scope}" (world | project)`)
        }

        const created = await createSkill(repo, {
          worldId: opts.worldId,
          projectId: opts.projectId,
          title,
          description,
          instructions,
          ...(given ? { documentId: given } : {}),
          skillId,
          scope,
        })
        const reach =
          scope === 'project'
            ? 'Every session in this project now sees it, and no other project'
            : 'Every session in this World now sees it'
        return (
          `Registered ${scope} skill "${title}" (${created.skillId}) — ` +
          `document ${created.documentId}. ${reach}; patch that document to ` +
          `change it.`
        )
      },
    },
  }
}

/**
 * The standing instructions alone, re-read from the World.
 *
 * SPLIT BY LIFETIME from the skills. Claude Code captures its system prompt
 * when a session is created and resumes that session on every later turn, so
 * anything recomputed per turn has to ride on the prompt. The skills change
 * between turns and ride there (`readSkillsTurnContext`); the standing
 * instructions do not, and go to the system prompt, sent once. The cost is that
 * a rule set mid-session reaches a running session on its next fresh start
 * rather than its next turn, which for a house rule is the right trade.
 *
 * Bounded and best-effort: a World that will not hydrate reads as none.
 */
export async function readWorldInstructions(
  repo: Repo,
  worldId: string | null | undefined,
  opts: { timeoutMs?: number } = {},
): Promise<string> {
  const world = await readDocBounded(repo, worldId, opts.timeoutMs)
  if (!world) return ''
  return formatWorldInstructions(
    await readStandingText(repo, world, 'world', opts.timeoutMs),
  )
}

/**
 * The World's and the project's standing instructions, in that order, re-read
 * (RFC 0334 3b). What every host puts after Mod's block, so the standing
 * prompt is Mod, World, project on every harness. Bounded and best-effort per
 * doc, like `readWorldInstructions`: one that will not hydrate reads as none.
 */
export async function readStandingInstructions(
  repo: Repo,
  worldId: string | null | undefined,
  projectId: string | null | undefined,
  opts: { timeoutMs?: number } = {},
): Promise<string> {
  const [world, project] = await Promise.all([
    readDocBounded(repo, worldId, opts.timeoutMs),
    readDocBounded(repo, projectId, opts.timeoutMs),
  ])
  // Each through its document once it has one (RFC 0334 3g). The World
  // root's AGENTS.md is the World's (RFC 0357 3i), so the home project does
  // not serve it again as its own.
  const homeIsWorld = !!projectId && isWorldSoulHome(world, projectId)
  const [worldText, projectText] = await Promise.all([
    world ? readStandingText(repo, world, 'world', opts.timeoutMs) : '',
    project && !homeIsWorld
      ? readStandingText(repo, project, 'project', opts.timeoutMs)
      : '',
  ])
  return formatStandingInstructions(worldText, projectText)
}

/**
 * The whole standing prompt a native harness gets (RFC 0334 3b): Mod's block
 * for the tools the session holds, then the World's instructions, then the
 * project's. The daemon hands this one string to every harness through that
 * harness's own channel, so the order cannot differ between them.
 */
export async function readStandingPrompt(
  repo: Repo,
  opts: {
    worldId: string | null | undefined
    projectId: string | null | undefined
    tools: Iterable<string>
    timeoutMs?: number
  },
): Promise<string> {
  const mod = formatModCapabilities(opts.tools)
  const instructions = await readStandingInstructions(
    repo,
    opts.worldId,
    opts.projectId,
    opts,
  )
  return [mod, instructions].filter(Boolean).join('\n\n')
}
